/**
 * Approvals decision drawer — bound to GET /approvals/{id}. Renders
 * agent, requester, "risk at request time", conversation, and a
 * comment composer + action footer. Orphan-aware.
 */
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { OwnerPill } from "@/components/owner-pill";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import {
  useApprovalDetail,
  usePostApprovalComment,
} from "@/hooks/security/useApprovals";
import {
  APPROVAL_STATE_LABEL,
  type ApprovalRequestDTO,
} from "@/types/lighthouseApprovals";
import {
  classificationClass,
  riskBarColor,
  stateChipClass,
} from "./approvalsUi";

interface Props {
  id: string | null;
  onOpenChange: (open: boolean) => void;
  onApprove: (req: ApprovalRequestDTO) => void;
  onDeny: (req: ApprovalRequestDTO) => void;
  onInfo: (req: ApprovalRequestDTO) => void;
}

export function ApprovalDrawer({ id, onOpenChange, onApprove, onDeny, onInfo }: Props) {
  const open = !!id;
  const detail = useApprovalDetail(id);
  const r = detail.data ?? null;
  const agent = r?.agent;
  const orphan = agent?.found === false;
  const score = agent?.riskScore ?? 0;
  const snapshot = r?.riskSnapshot ?? {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] p-0 flex flex-col overflow-hidden"
      >
        <SheetHeader className="p-6 pb-4 border-b border-border">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Approval request
          </div>
          <SheetTitle className="text-lg">
            {agent?.displayName ?? r?.agentId ?? "Loading…"}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2 text-xs">
            <span>{agent?.canonicalPublisher ?? r?.publisher ?? "—"}</span>
            {r && (
              <>
                <span>·</span>
                <Badge variant="outline" className={stateChipClass(r.state)}>
                  {APPROVAL_STATE_LABEL[r.state] ?? r.state}
                </Badge>
                <span>· created {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}</span>
              </>
            )}
          </SheetDescription>
          {orphan && (
            <Badge variant="outline" className="bg-warning-muted text-warning border-warning/30 gap-1 self-start">
              <AlertTriangle className="h-3 w-3" /> Agent no longer on fleet
            </Badge>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {detail.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {r && (
            <>
              <Section label="Agent">
                <div className="text-sm text-foreground">{agent?.displayName ?? r.agentId}</div>
                {agent?.hostname && (
                  <div className="text-xs text-muted-foreground">on {agent.hostname}</div>
                )}
                <div className="mt-1.5">
                  <OwnerPill consoleUser={agent?.consoleUser} size="sm" />
                </div>
              </Section>

              <Section label="Requested by">
                <div className="text-sm text-foreground">
                  {r.requester === "system" ? "Smplify (auto)" : r.requester ?? "—"}
                </div>
                {r.requestReason && (
                  <div className="text-xs text-muted-foreground mt-1">{r.requestReason}</div>
                )}
              </Section>

              {!orphan && (
                <Section label="Current risk & classification">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-light tabular-nums">{score}</div>
                    <div className="flex-1">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={cn("h-full", riskBarColor(score))} style={{ width: `${score}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Last seen {agent?.lastSeenAt ? formatDistanceToNow(new Date(agent.lastSeenAt), { addSuffix: true }) : "—"}
                      </div>
                    </div>
                    <Badge variant="outline" className={cn("capitalize", classificationClass(agent?.classification))}>
                      {agent?.classification ?? "unknown"}
                    </Badge>
                  </div>
                  <div className="mt-3 text-[11px] text-muted-foreground">
                    Attestation: {agent?.attestationManifestPath ? "Present" : "Missing"}
                  </div>
                </Section>
              )}

              <Section label="Risk at request time">
                <SnapshotGrid snapshot={snapshot} />
              </Section>

              <Section label="Conversation">
                <Conversation comments={r.comments ?? []} />
                <CommentCompose id={r.id} />
              </Section>
            </>
          )}
        </div>

        {r && (
          <div className="border-t border-border p-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onInfo(r)} disabled={orphan || r.state !== "pending"}>
              Ask info
            </Button>
            <Button variant="destructive" onClick={() => onDeny(r)} disabled={orphan || (r.state !== "pending" && r.state !== "info_requested")}>
              Deny
            </Button>
            <Button onClick={() => onApprove(r)} disabled={orphan || (r.state !== "pending" && r.state !== "info_requested")}>
              Approve with conditions
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function SnapshotGrid({ snapshot }: { snapshot: Record<string, unknown> }) {
  const total = (snapshot.totalScore as number | undefined) ?? null;
  const items: Array<[string, number | undefined]> = [
    ["Reach", snapshot.reach as number | undefined],
    ["Exposure", snapshot.exposure as number | undefined],
    ["Behavior", snapshot.behavior as number | undefined],
    ["Posture", snapshot.posture as number | undefined],
  ];
  const hasAny = total != null || items.some(([, v]) => typeof v === "number");
  if (!hasAny) {
    return <div className="text-xs text-muted-foreground">No snapshot captured.</div>;
  }
  return (
    <div className="space-y-2">
      {total != null && (
        <div className="text-sm tabular-nums">Total: {total}</div>
      )}
      <div className="grid grid-cols-4 gap-2 text-xs">
        {items.map(([k, v]) => (
          <div key={k} className="rounded border border-border px-2 py-1.5">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{k}</div>
            <div className="tabular-nums text-foreground">{v ?? "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Conversation({ comments }: { comments: Array<{ id: string; author: string; kind: string; body: string; createdAt: string }> }) {
  if (comments.length === 0) {
    return <div className="text-xs text-muted-foreground">No activity yet.</div>;
  }
  return (
    <ol className="space-y-3">
      {comments.map((c) => (
        <li key={c.id} className="text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="tabular-nums">{format(new Date(c.createdAt), "yyyy-MM-dd HH:mm")}</span>
            <span>·</span>
            <span className="text-foreground">{c.author}</span>
            <Badge variant="outline" className="text-[10px]">{c.kind}</Badge>
          </div>
          <div className="mt-1 text-foreground whitespace-pre-wrap">{c.body}</div>
        </li>
      ))}
    </ol>
  );
}

function CommentCompose({ id }: { id: string }) {
  const [body, setBody] = useState("");
  const mut = usePostApprovalComment();
  return (
    <div className="mt-3 space-y-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder="Type a comment…"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!body.trim() || mut.isPending}
          onClick={() =>
            mut.mutate(
              { id, body, kind: "comment" },
              { onSuccess: () => setBody("") },
            )
          }
        >
          Send
        </Button>
      </div>
    </div>
  );
}
